/**
 * GA4 / Segment context for server-side (Cloud Mode) attribution.
 *
 * When Segment sends events to GA4 via Measurement Protocol, GA4 needs
 * client_id, session_id, page referrer, page url, and userAgent so events
 * are not "Unassigned" and source/medium are set.
 *
 * This module reads _ga / _ga_* cookies and current page context so
 * track() calls can include the required data.
 */

const GA_COOKIE_PREFIX = '_ga';
const GA_SESSION_PREFIX = '_ga_';

/**
 * Parse _ga cookie value to get GA client_id.
 * Format: GA1.1.<part1>.<part2> → client_id = part1.part2
 */
const getGa4ClientId = () => {
  if (typeof document === 'undefined' || !document.cookie) return null;
  const match = document.cookie.match(/_ga=([^;]+)/);
  if (!match) return null;
  const value = match[1].trim();
  const parts = value.split('.');
  if (parts.length >= 4) {
    return `${parts[2]}.${parts[3]}`;
  }
  return null;
};

/**
 * Parse first _ga_<container> cookie to get session_id.
 * GA4 _ga_* value format: GS2.1.<session_start_ts>.* → session_id = session_start_ts
 */
const getGa4SessionId = () => {
  if (typeof document === 'undefined' || !document.cookie) return null;
  const cookies = document.cookie.split(';');
  for (let i = 0; i < cookies.length; i += 1) {
    const cookie = cookies[i].trim();
    if (cookie.indexOf(GA_SESSION_PREFIX) === 0) {
      const eq = cookie.indexOf('=');
      if (eq === -1) continue;
      const value = cookie.slice(eq + 1).trim();
      const parts = value.split('.');
      if (parts.length >= 4) {
        return parts[2];
      }
      return null;
    }
  }
  return null;
};

/**
 * Return referrer and url for the current page (and optionally landing, from storage).
 * Used as context.page.referrer and context.page.url in Segment track().
 */
const getPageContext = () => {
  if (typeof window === 'undefined') return { referrer: undefined, url: undefined };
  try {
    const landing = sessionStorage.getItem('segment_landing_context');
    const parsed = landing ? JSON.parse(landing) : {};
    return {
      referrer: parsed.referrer || document.referrer || undefined,
      url: window.location.href || undefined,
    };
  } catch (e) {
    return {
      referrer: document.referrer || undefined,
      url: window.location.href || undefined,
    };
  }
};

/**
 * Store landing referrer and URL once per session so we can send them with conversion events.
 * Call on app load (e.g. when URL/search changes) so we persist first-touch referrer.
 */
export const captureLandingContext = () => {
  if (typeof window === 'undefined') return;
  try {
    if (sessionStorage.getItem('segment_landing_context')) return;
    sessionStorage.setItem(
      'segment_landing_context',
      JSON.stringify({
        referrer: document.referrer || '',
        url: window.location.href || '',
      }),
    );
  } catch (e) {
    // ignore
  }
};

/**
 * Build full context object for Segment track() so GA4 Cloud can attribute the event.
 * Use this as the options.context when calling analytics.track(event, properties, { context }).
 *
 * Returns:
 * - context.page.referrer, context.page.url
 * - context.userAgent
 * - context.client_id, context.session_id (from GA cookies)
 */
export const getSegmentContextForGa4 = () => {
  if (typeof window === 'undefined') return {};
  const clientId = getGa4ClientId();
  const sessionId = getGa4SessionId();
  const page = getPageContext();
  const context = {
    page: {
      referrer: page.referrer,
      url: page.url,
    },
    userAgent: navigator.userAgent || undefined,
  };
  if (clientId) context.client_id = clientId;
  if (sessionId) context.session_id = sessionId;
  return context;
};
